import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { buildAgentLinks } from '../components/AgentLauncher'
import Footer from '../components/Footer'
import LivePreview from '../components/LivePreview'
import PromptBlock, { renderPromptText } from '../components/PromptBlock'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import TemplateCard from '../components/TemplateCard'
import TemplateComments from '../components/TemplateComments'
import { useCopyPrompt } from '../hooks/useCopyPrompt'
import { toCardData, toTemplateSpec } from '../lib/templateMap'
import type { TemplateCardData, TemplateSpec } from '../lib/templateTypes'
import { templatesApi, type TemplateSummaryDTO } from '../services/api'
import '../styles/templates.css'

/** The 4 steps — install is explicit; also emitted as HowTo JSON-LD. */
const HOW_IT_GOES_LIVE = [
  'Copy the prompt (or open it in your agent).',
  'Your agent reads opendeploy.dev/start and installs OpenDeploy if needed.',
  'It builds the site as a new project in a new directory.',
  'It asks, you approve — /opendeploy puts it live at your-site.opendeploy.site.',
]

function buildHowToJsonLd(template: TemplateSpec) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    '@id': `https://opendeploy.dev/templates/${template.slug}#howto`,
    name: `How to launch the ${template.name} website template with your coding agent`,
    description: template.description,
    step: HOW_IT_GOES_LIVE.map((text, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      text,
    })),
  }
}

function buildCreativeWorkJsonLd(template: TemplateSpec) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    '@id': `https://opendeploy.dev/templates/${template.slug}#template`,
    name: `${template.name} website template`,
    description: template.description,
    url: `https://opendeploy.dev/templates/${template.slug}`,
    image: `https://opendeploy.dev${template.ogImage}`,
    dateCreated: template.date,
    dateModified: template.updated ?? template.date,
    inLanguage: 'en',
    author: { '@id': 'https://opendeploy.dev/#organization' },
    publisher: { '@id': 'https://opendeploy.dev/#organization' },
  }
}

/** Mini copy chip for a follow-up prompt — its own copy chain per chip. */
function FollowUpChip({ prompt }: { prompt: string }) {
  const { copied, copyFailed, copy } = useCopyPrompt()
  const textRef = useRef<HTMLSpanElement>(null)

  // Total copy failure: select the chip text so "press ⌘C" works.
  useEffect(() => {
    if (!copyFailed) return
    const node = textRef.current
    const selection = window.getSelection()
    if (!node || !selection) return

    const range = document.createRange()
    range.selectNodeContents(node)
    selection.removeAllRanges()
    selection.addRange(range)
  }, [copyFailed])

  return (
    <button type="button" className="od-tpl-chip" onClick={() => copy(prompt)}>
      <span ref={textRef} className="od-tpl-chip-text">
        {prompt}
      </span>
      <span className="od-tpl-chip-state">
        {copyFailed ? 'press ⌘C' : copied ? 'copied' : 'copy'}
      </span>
      <span className="v2-sr-only" aria-live="polite">
        {copied ? 'Prompt copied' : ''}
      </span>
    </button>
  )
}

export default function TemplateDetail() {
  const { slug = '' } = useParams<{ slug: string }>()
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; template: TemplateSpec; related: TemplateCardData[] }
    | { status: 'missing' }
  >({ status: 'loading' })

  useEffect(() => {
    let active = true
    setState({ status: 'loading' })
    Promise.all([templatesApi.get(slug), templatesApi.list().catch(() => null)])
      .then(([detailRes, listRes]) => {
        if (!active) return
        const template = toTemplateSpec(detailRes.data)
        const related = (listRes?.data ?? [])
          .filter(
            (other: TemplateSummaryDTO) =>
              other.category === template.category && other.slug !== template.slug,
          )
          .slice(0, 4)
          .map(toCardData)
        setState({ status: 'ready', template, related })
      })
      .catch(() => {
        if (active) setState({ status: 'missing' })
      })
    return () => {
      active = false
    }
  }, [slug])

  if (state.status === 'missing') {
    return <Navigate to="/templates" replace state={{ missingSlug: slug }} />
  }

  if (state.status === 'loading') {
    return (
      <div className="od-tpl-page">
        <SiteNav />
        <main className="od-tpl-frame">
          <p className="od-tpl-gallery-status" role="status">
            Loading template…
          </p>
        </main>
        <Footer />
      </div>
    )
  }

  // Keyed by slug: navigating via "Related templates" re-renders this same
  // mounted route, so per-template state (PromptBlock's post-copy strip /
  // expanded state, comment thread) must reset with the template.
  return (
    <TemplateDetailView
      key={state.template.slug}
      template={state.template}
      related={state.related}
    />
  )
}

function TemplateDetailView({
  template,
  related,
}: {
  template: TemplateSpec
  related: TemplateCardData[]
}) {
  const agentLinks = buildAgentLinks(template.prompt)
  const lastBuildIndex = template.builds.length - 1

  return (
    <>
      <SEO
        title={`${template.name} website template — prompt for your coding agent | OpenDeploy`}
        description={template.description}
        path={`/templates/${template.slug}`}
        image={template.ogImage}
        jsonLd={[buildHowToJsonLd(template), buildCreativeWorkJsonLd(template)]}
      />

      <div className="od-tpl-page">
        <SiteNav />
        <main className="od-tpl-frame">
          <nav className="od-tpl-breadcrumb" aria-label="Breadcrumb">
            <Link to="/templates">← all templates</Link>
          </nav>

          <header className="od-tpl-detail-head">
            <div className="od-tpl-detail-title">
              <h1>{template.name}</h1>
              <span className="od-tpl-cat-chip">{template.category.toLowerCase()}</span>
            </div>
            <p className="od-tpl-detail-line">{template.oneLiner}</p>
          </header>

          <div className="od-tpl-detail-grid">
            <div className="od-tpl-detail-preview">
              {template.liveDemoUrl ? (
                <a
                  className="od-tpl-preview-frame od-tpl-preview-link"
                  href={template.liveDemoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open the live ${template.name} demo in a new tab`}
                >
                  <LivePreview
                    url={template.liveDemoUrl}
                    title={`Live ${template.name} demo`}
                    eager
                  />
                </a>
              ) : (
                <div className="od-tpl-preview-frame">
                  <span className="od-tpl-img-fallback">{template.name}</span>
                </div>
              )}
              {template.liveDemoUrl && (
                <a
                  className="od-tpl-demo-link"
                  href={template.liveDemoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  view live demo <span aria-hidden="true">↗</span>
                </a>
              )}
            </div>

            <aside className="od-tpl-rail" aria-label="Use this template">
              <PromptBlock slug={template.slug} prompt={template.prompt} />

              <section className="od-tpl-section" aria-labelledby="od-tpl-followups-title">
                <h2 id="od-tpl-followups-title">Keep prompting</h2>
                <div className="od-tpl-chips">
                  {template.followUpPrompts.map((followUp) => (
                    <FollowUpChip key={followUp} prompt={followUp} />
                  ))}
                </div>
              </section>

              <div className="od-tpl-rail-links">
                <span className="od-tpl-rail-links-label">open in</span>
                {agentLinks.map((agent) => (
                  <a
                    key={agent.id}
                    className="od-tpl-agent-link"
                    href={agent.href}
                    target={agent.href.startsWith('https://') ? '_blank' : undefined}
                    rel={
                      agent.href.startsWith('https://')
                        ? 'noopener noreferrer'
                        : undefined
                    }
                  >
                    <img src={agent.icon} alt="" decoding="async" />
                    <span>{agent.label}</span>
                    <span className="od-tpl-agent-arrow" aria-hidden="true">↗</span>
                  </a>
                ))}
              </div>

              <p className="od-tpl-rail-note">
                Works in Claude Code, Codex, Cursor, and any CLI agent. Your
                first deploy is free — no account needed.
              </p>
            </aside>

            <div className="od-tpl-detail-rest">
              <section className="od-tpl-section" aria-labelledby="od-tpl-builds-title">
                <h2 id="od-tpl-builds-title">What your agent builds</h2>
                <ul className="od-tpl-builds">
                  {template.builds.map((item, index) => (
                    <li
                      key={item}
                      className={
                        template.needsDatabase && index === lastBuildIndex
                          ? 'is-db'
                          : undefined
                      }
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="od-tpl-section" aria-labelledby="od-tpl-steps-title">
                <h2 id="od-tpl-steps-title">How it goes live</h2>
                <ol className="od-tpl-steps">
                  {HOW_IT_GOES_LIVE.map((step, index) => (
                    <li key={step}>
                      <span className="od-tpl-step-num" aria-hidden="true">
                        {index + 1}
                      </span>
                      <span>{renderPromptText(step)}</span>
                    </li>
                  ))}
                </ol>
              </section>

              <TemplateComments slug={template.slug} />

              {related.length > 0 && (
                <section className="od-tpl-section" aria-labelledby="od-tpl-related-title">
                  <h2 id="od-tpl-related-title">Related templates</h2>
                  <div className="od-tpl-related-grid">
                    {related.map((other) => (
                      <TemplateCard key={other.slug} template={other} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  )
}
